import { useMemo, useRef, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import useTable from '@/hooks/useTable';
import type { GsStory } from '@/lib/types';
import { formatPublished } from '@/lib/format';
import DemoBadge from '@/components/feature/DemoBadge';
import StoryStudio from './story-studio/StoryStudio';

type Row = {
  key: string;
  title: string;
  category: string;
  date: string;
  status: 'Published' | 'Draft';
};

const fieldClass = 'w-full text-sm border border-background-300 rounded-md px-3.5 py-2.5 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400';
const labelClass = 'block font-label uppercase tracking-widest text-[11px] text-foreground-600 mb-1.5';

export default function StoryManager() {
  const { data: stories } = useTable<GsStory>('gs_stories');
  const [added, setAdded] = useState<Row[]>([]);
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState('');
  const [flash, setFlash] = useState('');
  const [studioOpen, setStudioOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [showForm, setShowForm] = useState(false);

  const seeded: Row[] = useMemo(
    () => stories.map((s) => ({
      key: `db-${s.id}`,
      title: s.title,
      category: s.category,
      date: s.published_at || '',
      status: 'Published' as const,
    })),
    [stories],
  );

  const rows = useMemo(() => [...added, ...seeded].filter((r) => !removed.has(r.key)), [added, seeded, removed]);

  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setFlash('Image preview only — nothing is uploaded in this demo.');
    setTimeout(() => setFlash(''), 3500);
  };

  const createArticle = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const row: Row = {
      key: `local-${Date.now()}`,
      title: String(data.get('title') || 'Untitled article'),
      category: String(data.get('category') || 'News'),
      date: new Date().toISOString(),
      status: 'Draft',
    };
    setAdded((prev) => [row, ...prev]);
    setFlash('Draft created. In the live product you would review it here and publish it to the site.');
    setTimeout(() => setFlash(''), 4000);
    form.reset();
    setPreview('');
    if (fileRef.current) fileRef.current.value = '';
    setShowForm(false);
  };

  const toggle = (key: string) => {
    setAdded((prev) => prev.map((r) => (r.key === key ? { ...r, status: r.status === 'Published' ? 'Draft' : 'Published' } : r)));
    if (key.startsWith('db-')) {
      setFlash('That article is stored in the Backend — this toggle is a visual preview and does not change the live site.');
      setTimeout(() => setFlash(''), 4000);
    }
  };

  return (
    <section>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <h2 className="font-heading font-extrabold uppercase text-xl md:text-2xl text-foreground-950">News &amp; articles</h2>
        <DemoBadge label="Demo — not saved" />
        {!studioOpen && (
          <div className="ml-auto flex flex-wrap gap-2.5">
            <button
              type="button"
              onClick={() => setShowForm((s) => !s)}
              className="inline-flex items-center gap-2 border border-background-300 hover:border-foreground-700 text-foreground-700 font-label font-bold uppercase tracking-wider text-xs px-4 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className={showForm ? 'ri-close-line' : 'ri-add-line'}></i></span>
              {showForm ? 'Close' : 'New story'}
            </button>
            <button
              type="button"
              onClick={() => setStudioOpen(true)}
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 font-label font-bold uppercase tracking-wider text-xs px-4 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-magic-line"></i></span>
              Upload story &amp; prepare with AI
            </button>
          </div>
        )}
      </div>

      {studioOpen && <StoryStudio onExit={() => setStudioOpen(false)} />}

      {!studioOpen && (
      <>
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        {showForm && (
        <div className="xl:col-span-5">
          <form onSubmit={createArticle} className="bg-background-50 border border-background-200 rounded-lg p-5">
            <h3 className="font-heading font-bold text-xl text-foreground-950">Create a news article</h3>
            <div className="mt-4 space-y-3">
              <div>
                <label htmlFor="a-title" className={labelClass}>Headline</label>
                <input id="a-title" name="title" required placeholder="e.g. Sample course review" className={fieldClass} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="a-category" className={labelClass}>Category</label>
                  <input id="a-category" name="category" placeholder="e.g. Course Reviews" className={fieldClass} />
                </div>
                <div>
                  <label htmlFor="a-author" className={labelClass}>Author</label>
                  <input id="a-author" name="author" placeholder="Byline" className={fieldClass} />
                </div>
              </div>
              <div>
                <label htmlFor="a-excerpt" className={labelClass}>Summary</label>
                <textarea id="a-excerpt" name="excerpt" rows={2} maxLength={500} placeholder="Short summary (max 500 characters)" className={`${fieldClass} resize-none`}></textarea>
              </div>
              <div>
                <span className={labelClass}>Article image</span>
                <input
                  ref={fileRef}
                  id="a-image"
                  name="image"
                  type="file"
                  accept="image/*"
                  onChange={onFile}
                  className="w-full text-sm text-foreground-600 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-foreground-950 file:text-background-50 file:font-label file:uppercase file:tracking-wider file:text-xs file:cursor-pointer cursor-pointer"
                />
                {preview && (
                  <div className="mt-3 w-full h-40 rounded-md overflow-hidden border border-background-200">
                    <img src={preview} alt="Selected article image preview" className="w-full h-full object-cover object-top" />
                  </div>
                )}
              </div>
            </div>
            <button type="submit" className="mt-4 w-full bg-primary-500 hover:bg-primary-600 text-background-50 font-label font-bold uppercase tracking-wider py-3 rounded-md whitespace-nowrap cursor-pointer transition-colors">
              Save draft
            </button>
          </form>
        </div>
        )}

        <div className={showForm ? 'xl:col-span-7' : 'xl:col-span-12'}>
          <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
            <div className="px-5 py-3 border-b border-background-200 flex items-center justify-between">
              <p className="font-label uppercase tracking-widest text-[11px] text-foreground-500">{rows.length} articles</p>
              <p className="font-label uppercase tracking-widest text-[11px] text-foreground-500">Edit &amp; unpublish</p>
            </div>
            <ul className="divide-y divide-background-200 max-h-[460px] overflow-y-auto">
              {rows.map((r) => (
                <li key={r.key} className="px-5 py-3.5 flex items-center gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground-950 truncate">{r.title}</p>
                    <p className="text-xs text-foreground-500 truncate">{r.category}{r.date ? ` \u00b7 ${formatPublished(r.date)}` : ''}</p>
                  </div>
                  <span className={`font-label font-semibold uppercase text-[10px] tracking-wide px-2.5 py-1 rounded-sm whitespace-nowrap ${r.status === 'Published' ? 'bg-primary-500 text-background-50' : 'bg-accent-100 text-accent-900 border border-accent-300'}`}>{r.status}</span>
                  <button type="button" onClick={() => toggle(r.key)} className="font-label uppercase tracking-wide text-[11px] font-semibold text-foreground-700 hover:text-primary-600 cursor-pointer whitespace-nowrap">
                    {r.status === 'Published' ? 'Unpublish' : 'Publish'}
                  </button>
                  <button type="button" onClick={() => setRemoved((prev) => new Set(prev).add(r.key))} aria-label="Remove" className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-secondary-700 hover:bg-background-100 cursor-pointer">
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {flash && (
        <p className="mt-4 bg-secondary-50 border border-secondary-200 text-secondary-900 text-sm rounded-md px-4 py-3 flex items-start gap-2">
          <span className="w-4 h-4 flex items-center justify-center mt-0.5"><i className="ri-information-line"></i></span>{flash}
        </p>
      )}
      </>
      )}
    </section>
  );
}